// What the sync chip says, and the machine that decides it.
//
// The states as a function of the observations — how many writes are
// unacknowledged, whether there is a network, what the server has refused, and
// the read and earlier-session counts below — plus one piece of state no
// listener reports: which flash a timer is currently holding open.
//
// ── Why there are timers at all ─────────────────────────────────────────────
//
// Firestore acknowledges a write in milliseconds. Without an explicit hold the
// chip would go pending → clean inside one frame and a teacher would see
// nothing happen when they marked a lesson. So "syncing" and "saved" are held
// open for a fixed time after the fact they describe has passed.
//
// ── Why this is a value and not a React effect (#81) ────────────────────────
//
// Transitions are pure functions on an immutable value, the hold time is read
// off the state rather than scheduled at the transition, and connectivity is
// an event that moves `online` and nothing else. A driver that schedules
// `holdMillis` and re-schedules only when it changes cannot cancel a hold it
// did not start — an effect keyed on the count and connectivity whose cleanup
// cleared the timers could strand "Saved" or "Syncing…". The same shape as
// `android/.../data/SyncStatus.kt` and the iOS `SyncStatus.swift`
// (`docs/mobile/01-ios.md` §2).
//
// ── Which state wins is the core's (#682) ───────────────────────────────────
//
// `syncState` asks `core/src/sync_chip.rs`'s `chip_state`, which the web and
// both phones share, so a state is ranked the same on all three. This file
// keeps the counts, the events that move them, and the timers.

import { coreSync } from '../domain/core.js';

/** How long the "Syncing…" indicator stays up after pending writes clear. */
export const SYNCING_FLASH_MS = 1200;

// How long the green "Saved" confirmation lingers before fading to clean.
export const SAVED_FLASH_MS = 2000;

/** The part of the state no listener reports: what the timer is holding. */
export const FLASH = { none: 'none', syncing: 'syncing', saved: 'saved' };

/**
 * A machine at rest. `online` is an observation, so the caller supplies it.
 *
 * `refusedCount` and `refusedSubject` are the sixth state (#162): how many
 * writes the server has refused since the teacher last dismissed the news, and
 * what they were about — a collection noun like "student", or null when the
 * refusals do not agree on one. See `onWriteRefused`.
 */
export function syncMachine(online = true) {
  return {
    pendingCount: 0,
    online,
    flash: FLASH.none,
    refusedCount: 0,
    refusedSubject: null,
    // Writes from an earlier session that the SDK's queue has since drained,
    // whose outcome nobody can now know (#167). See `onWritesUnconfirmed`.
    unconfirmedCount: 0,
    // How many read scopes are being served out of the local store with no
    // snapshot behind them in this session (#207 U6). See `onStaleReads`, and
    // `src/data/staleReads.js` for where the number comes from: the store's
    // scopes, and on the snapshot path each listener that has answered only
    // from the SDK's cache this session (#682).
    staleReadCount: 0,
    // How many lists are waiting for the server (#682) — answered only by a
    // cache that has never had the server's answer for them, and holding
    // nothing (#651, `src/data/serverAnswers.js`). See `onAwaitingServer`.
    awaitingCount: 0,
  };
}

// Two machines are the same machine if all the fields match. Used to return
// the existing object when a transition changes nothing: `useState` bails out
// of a re-render on `Object.is`, and SyncProvider wraps the entire app, so a
// snapshot that reports the same count must not re-render it.
const same = (a, b) =>
  a.pendingCount === b.pendingCount &&
  a.online === b.online &&
  a.flash === b.flash &&
  a.refusedCount === b.refusedCount &&
  a.refusedSubject === b.refusedSubject &&
  a.unconfirmedCount === b.unconfirmedCount &&
  a.staleReadCount === b.staleReadCount &&
  a.awaitingCount === b.awaitingCount;

const settle = (m, next) => (same(m, next) ? m : next);

/**
 * Refused > unconfirmed > offline > awaiting > stale > pending > syncing >
 * saved > clean. Pending before syncing because a new write arriving mid-flash
 * means the flash is stale; offline above the write states because a queued
 * write that cannot leave the laptop is a fact about the network.
 *
 * Refused above even offline (#162): it is the only state that reports a loss
 * and does not clear itself, and the refused row has already vanished from
 * its list, so a teacher has no other way to find out. It stays until
 * dismissed. Unconfirmed sits just under it (#167) for the same reason; "the
 * server said no" outranks "nobody can say".
 *
 * `awaiting` (#682): some list has answered only from a cache that never had
 * the server's answer, and holds nothing (#651). Above `stale` because it is
 * the stronger statement; under `offline`, which explains the wait. Its words
 * do not say "offline": the device may be online with the server unreachable.
 *
 * `stale` (#207 U6): what is on screen may not be what the server holds, and
 * only a snapshot landing ends it. Under `offline`, which already explains it;
 * above `pending`, which is reassuring and clears itself. It is for the case
 * with no other symptom: online and stale, a client whose listeners have not
 * delivered (#71, #203).
 *
 * The order is the core's (`core/src/sync_chip.rs`, which has the argument);
 * this passes the machine's facts across and returns the state's name.
 */
export function syncState(m) {
  const n = (x) => Math.max(0, Number(x) || 0);
  return coreSync().syncChipState(
    n(m.refusedCount), n(m.unconfirmedCount), Boolean(m.online), n(m.awaitingCount),
    n(m.staleReadCount), n(m.pendingCount),
    m.flash === FLASH.syncing, m.flash === FLASH.saved,
  );
}

/**
 * How long the driver should wait before calling `onHoldExpired`, or null if
 * nothing is being held open. Reading the delay off the state keeps this file
 * free of a clock, and lets the driver key its timer on this value alone.
 *
 * `refused` is not a flash: `onWriteRefused` clears the flash and
 * `onPendingWrites` will not start one while a refusal stands, so a refused
 * machine reports null and nothing scheduled can take the news off the screen.
 */
export function holdMillis(m) {
  if (m.flash === FLASH.syncing) return SYNCING_FLASH_MS;
  if (m.flash === FLASH.saved) return SAVED_FLASH_MS;
  return null;
}

/**
 * A new count of unacknowledged writes.
 *
 * The falling edge — writes were outstanding, they are not any more, and we
 * are online — is the only way into the flash. Anything still outstanding
 * aborts a flash in progress, so the chip never says "Saved" while a write
 * sits in the cache.
 *
 * Clearing while offline starts nothing: the count can only fall offline if
 * the writes were dropped or the listener was torn down.
 *
 * Nor while a refusal or an unconfirmed note stands (#162). A write the server
 * refuses is rolled back out of the local cache, so the count drops exactly as
 * for a success. The SDK rejects the user callback before it emits the
 * snapshot (`syncEngineRejectFailedWrite`), so `refusedCount` is already up
 * when this branch sees the count fall.
 */
export function onPendingWrites(m, n) {
  if (n > 0) return settle(m, { ...m, pendingCount: n, flash: FLASH.none });
  if (m.pendingCount > 0 && m.online && m.refusedCount === 0 && m.unconfirmedCount === 0) {
    return { ...m, pendingCount: 0, flash: FLASH.syncing };
  }
  return settle(m, { ...m, pendingCount: 0 });
}

/**
 * The server refused a write, permanently (#162).
 *
 * `subject` is a collection noun for the row it was about — "student",
 * "expense" — or null where the reporter could not name one. A second refusal
 * about something else drops the subject rather than picking a winner, since
 * the refusals may be a student and an invoice.
 *
 * Sticky: only `onRefusalsDismissed` clears it. A later write that succeeds is
 * not evidence about the refused one. The flash is dropped.
 */
export function onWriteRefused(m, subject = null) {
  const first = m.refusedCount === 0;
  return {
    ...m,
    flash: FLASH.none,
    refusedCount: m.refusedCount + 1,
    refusedSubject: first || m.refusedSubject === subject ? subject : null,
  };
}

/** The teacher has read the bad news. Only this clears it. */
export function onRefusalsDismissed(m) {
  return settle(m, { ...m, refusedCount: 0, refusedSubject: null });
}

/**
 * Writes from an earlier session whose outcome cannot be recovered (#167).
 *
 * The SDK's mutation queue survives a reload; the callbacks that would have
 * told us the answer do not. So a drained queue with `outstandingWrites.js`
 * records from before this session means the answers were lost.
 *
 * "Unconfirmed", not "refused": reading the documents back cannot settle it,
 * because almost every write carries `serverTimestamp()`, null locally and a
 * real value once acknowledged, so a successful write reads back different.
 *
 * Sticky like `refused`.
 */
export function onWritesUnconfirmed(m, n) {
  if (!n) return m;
  return { ...m, flash: FLASH.none, unconfirmedCount: m.unconfirmedCount + n };
}

/** The teacher has read it. Only this clears it. */
export function onUnconfirmedDismissed(m) {
  return settle(m, { ...m, unconfirmedCount: 0 });
}

/**
 * A new count of read scopes being served from disk with no snapshot behind
 * them this session (#207 U6).
 *
 * A condition, not an event: no flash, no timer and nothing to dismiss; it
 * ends when a snapshot lands. It never blocks a render (#207 Q5).
 */
export function onStaleReads(m, n) {
  return settle(m, { ...m, staleReadCount: n });
}

/**
 * A new count of lists waiting for the server (#682): each answered only by a
 * cache that has never had the server's answer for it, holding nothing —
 * `awaitingServerCount()` in `src/data/serverAnswers.js`, the registry #651's
 * *Not loaded on this device yet* is drawn from.
 *
 * A condition, like `onStaleReads`, with no flash and nothing to dismiss: it
 * ends when the server answers those lists, and the chip moves on its own.
 */
export function onAwaitingServer(m, n) {
  return settle(m, { ...m, awaitingCount: n });
}

/**
 * The network came or went. Only `online` moves: a running flash is about
 * writes that have already landed and is still true. `syncState` hides it
 * behind "Offline" meanwhile, and it is still there — or expired on its own
 * schedule — when the network returns.
 */
export function onConnectivity(m, online) {
  return settle(m, { ...m, online });
}

/** `holdMillis` elapsed: syncing hands over to saved, saved fades to clean. */
export function onHoldExpired(m) {
  if (m.flash === FLASH.syncing) return { ...m, flash: FLASH.saved };
  if (m.flash === FLASH.saved) return { ...m, flash: FLASH.none };
  return m;
}
